import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@luggagehero/shared/ui-pipes';

import { StoragePriceTier, StoragePriceTierId } from './storage-price-ladder.model';

/** The tiers the table has a row for: walk-in is a same-day booking, so it has none. */
type StoragePriceTableTierId = Exclude<StoragePriceTierId, 'walkIn'>;

/** The translation keys the price table renders. */
export interface StoragePriceTableCopy {
  /** Row labels, one per tier. */
  rows: Record<StoragePriceTableTierId, string>;
  /** Column headings. */
  perHour: string;
  perDay: string;
}

/** The traveler apps' keys. */
export const STORAGE_PRICE_TABLE_COPY: StoragePriceTableCopy = {
  rows: { advance: 'PRICE_TIER_TABLE_DAY_AHEAD', sameDay: 'PRICE_TIER_TABLE_SAME_DAY' },
  perHour: 'PER_HOUR',
  perDay: 'PRICE_TIER_PER_DAY',
};

/**
 * The landing app's keys. Landing hands only `STORES_*` keys from the server render to the browser
 * (server-app-text-loader.ts, AM-5056), so a key outside that namespace would flash raw at hydration.
 */
export const STORES_PRICE_TABLE_COPY: StoragePriceTableCopy = {
  rows: { advance: 'STORES_PRICE_TABLE_DAY_AHEAD', sameDay: 'STORES_PRICE_TABLE_SAME_DAY' },
  perHour: 'STORES_PRICE_TABLE_PER_HOUR',
  perDay: 'STORES_PRICE_TABLE_PER_DAY',
};

/**
 * Every storage price at a glance (CTR-51): a Per hour and a Per day column, and a row for booking by the
 * day before (Day ahead) and one for booking on the day (Same day). The landing store page shows it in its
 * "Prices per bag" card, and the traveler places page in its price details modal.
 *
 * Walk-in has no row: walking in is a same-day booking, so Same day covers it. A walk-in tier passed in is
 * left out. With a single row (same-day priced as advance) the row has no label or row header: "Day ahead"
 * would suggest booking today costs more when it does not, and the column headings name the prices.
 *
 * The caller decides which tiers exist (see `buildStoragePriceTiers`) and formats the amounts: the
 * landing card names the currency in its heading and passes bare amounts, the modal passes amounts
 * with their currency.
 */
@Component({
  selector: 'lh-shared-ui-storage-price-table',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <table class="price-table">
      <thead>
        <tr>
          <td></td>
          <th scope="col" class="price-table__heading" data-unit="hour">{{ copy().perHour | lhTranslate }}</th>
          <th scope="col" class="price-table__heading" data-unit="day">{{ copy().perDay | lhTranslate }}</th>
        </tr>
      </thead>
      <tbody>
        @for (tier of rows(); track tier.id) {
          <tr class="price-table__row" [attr.data-tier]="tier.id">
            @if (rows().length > 1) {
              <th scope="row" class="price-table__tier">{{ copy().rows[tier.id] | lhTranslate }}</th>
            } @else {
              <td class="price-table__tier"></td>
            }
            <td class="price-table__amount" [attr.data-tier]="tier.id" data-unit="hour">
              {{ formatAmount()(tier.hourly) }}
            </td>
            <td class="price-table__amount" [attr.data-tier]="tier.id" data-unit="day">
              {{ formatAmount()(tier.daily) }}
            </td>
          </tr>
        }
      </tbody>
    </table>
  `,
  styles: `
    :host {
      display: block;
      /* A narrow container scrolls the table rather than widening the page. */
      overflow-x: auto;
      color: var(--text-primary, #000);
    }
    .price-table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--font-size-base, 16px);
      font-variant-numeric: tabular-nums;
    }
    .price-table th,
    .price-table td {
      padding: 0;
      border: 0;
    }
    .price-table th.price-table__heading {
      padding: 0 0 var(--space-3, 12px) var(--space-2, 8px);
      text-align: right;
      font-size: var(--font-size-sm, 14px);
      font-weight: 500;
      color: var(--color-neutral-700, #525252);
      vertical-align: bottom;
      /* "Pro Stunde" stays one heading over its prices; a long row label wraps instead. */
      white-space: nowrap;
    }
    .price-table tr.price-table__row {
      border-top: 1px solid var(--color-neutral-200, #f3f3f3);
    }
    .price-table .price-table__tier {
      padding: var(--space-3, 12px) var(--space-2, 8px) var(--space-3, 12px) 0;
      text-align: left;
      font-weight: 400;
    }
    .price-table td.price-table__amount {
      padding: var(--space-3, 12px) 0 var(--space-3, 12px) var(--space-2, 8px);
      text-align: right;
      font-weight: 700;
      white-space: nowrap;
    }
  `,
})
export class SharedUiStoragePriceTableComponent {
  /** The tiers to show, in order. */
  readonly tiers = input.required<StoragePriceTier[]>();
  /** Formats one amount for a cell. */
  readonly formatAmount = input.required<(amount: number) => string>();
  /** The translation keys to render; the landing app passes its `STORES_*` set. */
  readonly copy = input<StoragePriceTableCopy>(STORAGE_PRICE_TABLE_COPY);

  /** The tiers that get a row: all but walk-in, which is a same-day booking. */
  readonly rows = computed(() =>
    this.tiers().filter((tier): tier is StoragePriceTier & { id: StoragePriceTableTierId } => tier.id !== 'walkIn'),
  );
}
